import React, { useMemo } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    CircularProgress,
    Alert,
} from '@mui/material';
import dayjs from 'dayjs';
import { useGetCustomerPastProducts } from '../../../../../services/api/queries/useGetCustomerPastProducts';
import { Order } from '../../../../../Models';
import { usePrescriptionData } from '../../../../../services/api/queries/usePrescriptionData';

type CustomerHistoryCardProps = {
    order: Order;
};

export function CustomerHistoryCard({ order }: CustomerHistoryCardProps) {
    const { pastProducts, isLoading, error } = useGetCustomerPastProducts(order.id);
    const { electronicProducts, otcProducts } = usePrescriptionData(order);

    // Get current order products for matching
    const currentProductAB = useMemo(
        () => new Set(electronicProducts.map((p) => p.alfabeta_code)),
        [electronicProducts],
    );

    const currentProductGenerics = useMemo(
        () => new Set(electronicProducts.map((p) => p.generic_name)),
        [electronicProducts],
    );

    const currentProductNames = useMemo(() => {
        const allCurrentProducts = [...electronicProducts, ...otcProducts];
        return new Set(allCurrentProducts.map((p) => p.name.toLowerCase()));
    }, [electronicProducts, otcProducts]);

    const isProductMatching = (
        productId: string,
        productName: string,
        productGenericName: string | undefined,
    ) =>
        currentProductAB.has(productId) ||
        currentProductNames.has(productName.toLowerCase()) ||
        (productGenericName && currentProductGenerics.has(productGenericName));

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={2}>
                <Alert severity="error">Error cargando el historial</Alert>
            </Box>
        );
    }

    if (!pastProducts.length) {
        return (
            <Box p={2}>
                <Typography variant="body2" color="text.secondary">
                    Aún no hay historial de productos para este cliente
                </Typography>
            </Box>
        );
    }

    return (
        <Box height="100%" display="flex" flexDirection="column">
            <Box p={2}>
                <Typography variant="h6" gutterBottom>
                    Historial del Cliente
                </Typography>
            </Box>
            <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Nombre</TableCell>
                            <TableCell>Genérico</TableCell>
                            <TableCell>Dto.</TableCell>
                            <TableCell>Cant.</TableCell>
                            <TableCell>Fecha</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pastProducts.map((product) => {
                            const isMatching = isProductMatching(
                                product.id,
                                product.name,
                                product.generic_name,
                            );
                            return (
                                <TableRow
                                    key={`${product.id}-${product.settlement_date}-${product.name}`}
                                    sx={{
                                        backgroundColor: isMatching ? '#e3f2fd' : 'inherit',
                                        '&:hover': {
                                            backgroundColor: isMatching
                                                ? '#bbdefb'
                                                : 'action.hover',
                                        },
                                    }}>
                                    <TableCell>{product.id}</TableCell>
                                    <TableCell>{product.name}</TableCell>
                                    <TableCell>{product.generic_name || '-'}</TableCell>
                                    <TableCell align="right">{product.discount_percent}%</TableCell>
                                    <TableCell align="right">{product.quantity}</TableCell>
                                    <TableCell>
                                        {dayjs(product.settlement_date).format('DD/MM/YY')}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
